import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { MapPin, Hospital, ShieldCheck, Wallet, Languages, AlertTriangle, Plane, HelpCircle } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { breadcrumbSchema, faqPageSchema, localizedHead, validateLangSearch } from "@/lib/seo";
import { RelatedLinks } from "@/components/related-links";
import { ConsultCta } from "@/components/consult-cta";
import { HeroImage } from "@/components/hero-image";
import { thailandClinicImage } from "@/lib/responsive-images";

interface ExpatFaq { q: string; a: string }

function getExpatFaqs(de: boolean): ExpatFaq[] {
  return de
    ? [
        { q: "Kann ich Retatrutid in Thailand erhalten?", a: 'Nicht als frei verkäufliches Fertigarzneimittel. Retatrutid (LY3437943) ist in Thailand (Stand 2026) nicht als Handelsarzneimittel zugelassen, aber über genehmigte klinische Studienprogramme – wie das unserer Partner-Klinik – für qualifizierte Teilnehmer zugänglich. Angebote als „Research Peptide" aus Bangkok oder China sind keine geprüften Arzneimittel und nicht Teil eines genehmigten Studienprogramms.' },
        { q: "Bekomme ich in Thailand ein Rezept für Wegovy oder Mounjaro als Ausländer?", a: "Ja. Internationale Krankenhäuser wie Bumrungrad, BNH, Samitivej und MedPark verschreiben Wegovy und Mounjaro auch Ausländern ohne thailändische Krankenversicherung. Sie brauchen lediglich Reisepass und ein Erstgespräch in der Endokrinologie- / Adipositas-Sprechstunde." },
        { q: "Was kostet eine GLP-1-Therapie in Thailand pro Monat?", a: "Richtwerte 2026: Ozempic ca. 6.500 – 9.000 THB, Wegovy ca. 8.000 – 12.000 THB, Mounjaro 5 mg ca. 12.000 – 18.000 THB pro Monat. Dazu kommen 1.500 – 3.000 THB für die Erstkonsultation. Preise variieren je nach Krankenhaus und Verfügbarkeit." },
        { q: "Übernimmt meine Expat-Krankenversicherung die Behandlung?", a: "In der Regel nein, wenn die Verordnung zur Gewichtsreduktion erfolgt. Cigna Global, April International und ähnliche Anbieter erstatten Konsultationen und Diagnostik oft, aber selten das Medikament selbst. Bei Typ-2-Diabetes als Indikation sind die Chancen deutlich besser – Verordnungsdiagnose immer schriftlich bestätigen lassen." },
        { q: "Darf ich Wegovy oder Mounjaro aus dem Ausland nach Thailand einführen?", a: "Der Import per Post ist ohne Import-Lizenz nicht vorgesehen. Im persönlichen Reisegepäck ist ein Vorrat für maximal 30 Tage plus ärztliches Rezept toleriert. Für Retatrutid organisiert das genehmigte Studienprogramm der Klinik die Versorgung; privater Import ist nicht der vorgesehene Weg." },
        { q: "Beeinflusst mein Visum (Retirement, Elite, Long-Stay) die Rezeptausstellung?", a: "Nein. Das Visum ist für den Zugang zu privaten internationalen Krankenhäusern nicht relevant. Sie brauchen weder eine thailändische Steuernummer noch eine Arbeitserlaubnis, um Wegovy, Ozempic oder Mounjaro verschrieben zu bekommen." },
        { q: "Gibt es deutschsprachige Ärzte in Bangkok für GLP-1-Therapien?", a: 'Punktuell ja – vor allem bei Bumrungrad International und Samitivej Sukhumvit. Für die Terminvereinbarung fragen Sie nach „German-speaking endocrinologist" oder „obesity clinic". Standardsprache ist ansonsten Englisch, das in allen genannten Krankenhäusern problemlos ausreicht.' },
        { q: "Ist die Kühlkette bei Hitze und Umzügen in Thailand ein Problem?", a: "Ja, dies ist der häufigste Praxis-Fehler. Ungeöffnete Pens gehören in den Kühlschrank (2–8 °C). Nach Anbruch sind Ozempic und Wegovy je nach Präparat bis zu 6 Wochen bei Raumtemperatur (max. 30 °C) haltbar, Mounjaro-Pens bis 21 Tage. Für längere Reisen innerhalb Thailands nutzen Sie Kühltaschen mit Gel-Akkus." },
      ]
    : [
        { q: "Can I get Retatrutide in Thailand?", a: "Not as an over-the-counter finished medicine. As of 2026, Retatrutide (LY3437943) is not registered as a commercial drug in Thailand, but it is available through approved clinical study programs — such as our partner clinic's — for eligible participants. Offers marketed as 'research peptides' from Bangkok or China are not verified medicines and not part of an approved study program." },
        { q: "Can I get a Wegovy or Mounjaro prescription in Thailand as a foreigner?", a: "Yes. International hospitals such as Bumrungrad, BNH, Samitivej and MedPark prescribe Wegovy and Mounjaro to foreigners without Thai health insurance. You only need your passport and an initial consultation at the endocrinology / obesity clinic." },
        { q: "How much does GLP-1 therapy cost per month in Thailand?", a: "2026 reference: Ozempic ~ 6,500 – 9,000 THB, Wegovy ~ 8,000 – 12,000 THB, Mounjaro 5 mg ~ 12,000 – 18,000 THB per month. Add 1,500 – 3,000 THB for the initial consultation. Prices vary by hospital and stock." },
        { q: "Will my expat health insurance cover the treatment?", a: "Usually no when the prescription is for weight loss. Cigna Global, April International and similar plans often reimburse consultations and diagnostics, but rarely the drug itself. With type-2 diabetes as the indication the odds improve significantly — always get the diagnosis noted in writing." },
        { q: "Can I import Wegovy or Mounjaro into Thailand from abroad?", a: "Postal import without a licence is not the intended route. In personal luggage, up to a 30-day supply plus a physician's prescription is tolerated. For Retatrutide, the approved study program at the clinic handles supply; private import is not the intended route." },
        { q: "Does my visa (Retirement, Elite, Long-Stay) affect the prescription?", a: "No. Your visa is not relevant for accessing private international hospitals. You need neither a Thai tax ID nor a work permit to be prescribed Wegovy, Ozempic or Mounjaro." },
        { q: "Are there German-speaking doctors in Bangkok for GLP-1 therapy?", a: "Occasionally yes — mainly at Bumrungrad International and Samitivej Sukhumvit. When booking, ask for a 'German-speaking endocrinologist' or 'obesity clinic'. Otherwise the default language is English, which is fully sufficient at every hospital listed." },
        { q: "Is the cold chain a problem in Thai heat and during moves?", a: "Yes — this is the most common real-world mistake. Unopened pens belong in a refrigerator (2–8 °C). Once in use, Ozempic and Wegovy stay stable at room temperature (max 30 °C) for up to 6 weeks depending on brand, Mounjaro pens for up to 21 days. For longer trips inside Thailand use insulated bags with gel packs." },
      ];
}

export const Route = createFileRoute("/thailand")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => {
    const base = localizedHead("/thailand", loaderData?.lang);
    const lang = loaderData?.lang ?? "en";
    const de = lang === "de";

    const medicalPage = {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      inLanguage: de ? "de-DE" : "en-US",
      name: de
        ? "Retatrutid & GLP-1 in Thailand – Guide für Expats"
        : "Retatrutide & GLP-1 in Thailand – expat guide",
      about: {
        "@type": "Drug",
        name: "Retatrutide",
        alternateName: ["Retatrutid", "LY3437943"],
      },
      audience: {
        "@type": "PeopleAudience",
        audienceType: de
          ? "Ausländer / Expats mit Wohnsitz in Thailand"
          : "Foreigners / expats residing in Thailand",
        geographicArea: { "@type": "Country", name: "Thailand", identifier: "TH" },
      },
      spatialCoverage: {
        "@type": "Place",
        name: "Thailand",
        address: { "@type": "PostalAddress", addressCountry: "TH" },
        geo: { "@type": "GeoCoordinates", latitude: 13.7563, longitude: 100.5018 },
        containsPlace: [
          { "@type": "City", name: "Bangkok" },
          { "@type": "City", name: "Chiang Mai" },
          { "@type": "City", name: "Phuket" },
          { "@type": "City", name: "Pattaya" },
        ],
      },
      areaServed: { "@type": "Country", name: "Thailand", identifier: "TH" },
      mainContentOfPage: de
        ? "Verfügbarkeit von Retatrutid, Wegovy, Ozempic und Mounjaro in Thailand, Thai FDA (อย.), internationale Krankenhäuser in Bangkok und Preise in Baht."
        : "Availability of Retatrutide, Wegovy, Ozempic and Mounjaro in Thailand, Thai FDA (อย.), international hospitals in Bangkok and prices in Baht.",
    };

    const hospitals = [
      { name: "Bumrungrad International Hospital", street: "33 Sukhumvit 3", city: "Bangkok", postal: "10110" },
      { name: "BNH Hospital", street: "9/1 Convent Rd, Silom", city: "Bangkok", postal: "10500" },
      { name: "Samitivej Sukhumvit Hospital", street: "133 Sukhumvit 49", city: "Bangkok", postal: "10110" },
      { name: "MedPark Hospital", street: "3333 Rama IV Rd, Khlong Toei", city: "Bangkok", postal: "10110" },
    ];

    const hospitalList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: de
        ? "Internationale Krankenhäuser in Bangkok mit GLP-1- / Adipositas-Sprechstunden"
        : "International hospitals in Bangkok with GLP-1 / obesity clinics",
      inLanguage: de ? "de-DE" : "en-US",
      numberOfItems: hospitals.length,
      itemListElement: hospitals.map((h, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Hospital",
          name: h.name,
          address: {
            "@type": "PostalAddress",
            streetAddress: h.street,
            addressLocality: h.city,
            postalCode: h.postal,
            addressCountry: "TH",
          },
          areaServed: { "@type": "Country", name: "Thailand" },
          medicalSpecialty: ["Endocrine", "Nutrition"],
        },
      })),
    };

    const breadcrumb = breadcrumbSchema("/thailand", loaderData?.lang);

    const faqs = getExpatFaqs(de);
    const faqPage = faqPageSchema("/thailand", loaderData?.lang, faqs, {
      about: { "@type": "Country", name: "Thailand", identifier: "TH" },
    });


    return {
      ...base,
      meta: [
        ...base.meta,
        { property: "og:locale", content: de ? "de_TH" : "en_TH" },
        { name: "geo.region", content: "TH-10" },
        { name: "geo.placename", content: "Bangkok" },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(medicalPage) },
        { type: "application/ld+json", children: JSON.stringify(hospitalList) },
        { type: "application/ld+json", children: JSON.stringify(faqPage) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
      ],
    };
  },
  component: Page,
});

function Page() {
  const { lang } = useI18n();
  const de = lang === "de";

  const c = de
    ? {
        eyebrow: "Für Expats in Thailand",
        h1: "Retatrutid & GLP-1-Therapien in Thailand",
        lead: "Was Ausländer, die in Bangkok, Chiang Mai, Phuket oder Pattaya leben, aktuell wissen müssen: Retatrutid ist in Thailand (Stand 2026) nicht als Fertigarzneimittel zugelassen, aber über genehmigte klinische Studienprogramme zugänglich. Wegovy, Ozempic und Mounjaro sind teilweise über internationale Krankenhäuser verfügbar.",
        statusTitle: "Zulassungs-Status in Thailand (อย. / Thai FDA)",
        status: [
          { name: "Retatrutid (LY3437943)", value: "Klinische Studie", ok: true, body: "Kein regulärer Apotheken- oder Krankenhausverkauf. Zugang ausschließlich über genehmigte klinische Studienprogramme, z. B. unsere Partner-Klinik." },
          { name: "Semaglutid (Ozempic, Wegovy)", value: "Verfügbar", ok: true, body: "Ozempic ist in Thailand als T2D-Medikation registriert; Wegovy ist über internationale Krankenhäuser wie Bumrungrad, BNH und Samitivej verfügbar." },
          { name: "Tirzepatid (Mounjaro)", value: "Eingeschränkt", ok: true, body: "Mounjaro wird von Eli Lilly Thailand seit 2024 vertrieben, ist aber häufig knapp. Zepbound-Marke ist in Thailand nicht separat registriert." },
        ],
        clinicsTitle: "Internationale Krankenhäuser in Bangkok mit Adipositas-Sprechstunden",
        clinics: [
          { name: "Bumrungrad International", area: "Sukhumvit / Nana", body: "Englischsprachige Endokrinologie- und Adipositas-Sprechstunden, teilweise deutsch- und arabischsprachige Koordinatoren." },
          { name: "BNH Hospital", area: "Silom / Sathorn", body: "Kleineres Expat-Krankenhaus, engmaschige Betreuung bei GLP-1-Therapien." },
          { name: "Samitivej Sukhumvit", area: "Phrom Phong", body: "Etablierte Anlaufstelle für langjährige Expats, japanisch- und englischsprachiges Personal." },
          { name: "MedPark Hospital", area: "Rama IV", body: "Modernes Krankenhaus mit Fokus auf metabolische Medizin und Diabetes." },
        ],
        priceTitle: "Preise in Thailand (Richtwerte 2026, in THB)",
        prices: [
          { drug: "Ozempic 1,0 mg (1 Pen / 4 Wochen)", price: "~ 6.500 – 9.000 THB" },
          { drug: "Wegovy (Startdosis)", price: "~ 8.000 – 12.000 THB / Monat" },
          { drug: "Mounjaro 5 mg (4 Pens / Monat)", price: "~ 12.000 – 18.000 THB" },
          { drug: "Arzt-Erstkonsultation (Bumrungrad / Samitivej)", price: "~ 1.500 – 3.000 THB" },
        ],
        priceNote:
          "Preise variieren je nach Krankenhaus, Dosis und Verfügbarkeit. Private Krankenversicherungen in Thailand übernehmen GLP-1-Therapien zur Gewichtsreduktion in der Regel NICHT – anders als bei Typ-2-Diabetes.",
        insuranceTitle: "Versicherung & Aufenthaltsstatus",
        insuranceBody:
          "Für ein Rezept in Thailand brauchen Sie keine thailändische Krankenversicherung – die Behandlung wird meist bar oder per Kreditkarte bezahlt. Sinnvoll ist eine internationale Expat-Versicherung (Cigna Global, April International, LuxMed etc.), die zumindest die Konsultationen abdeckt. Long-Stay-, Elite- oder Retirement-Visa haben keinen Einfluss auf die Rezeptausstellung.",
        legalTitle: "Rechtliche Hinweise",
        legalItems: [
          'Der Import von Wegovy / Ozempic / Mounjaro nach Thailand erfordert ein ärztliches Rezept; postalische Sendungen sind nicht vorgesehen.',
          '„Research Peptides" mit Retatrutid, die online aus China oder Thailand angeboten werden, sind keine geprüften Arzneimittel und nicht Teil eines genehmigten Studienprogramms.',
          "Für die Behandlung ist ein Rezept eines in Thailand zugelassenen Arztes erforderlich; nutzen Sie eines der genannten internationalen Krankenhäuser.",
        ],
        langTitle: "Sprache im Arztgespräch",
        langBody:
          'In den genannten Krankenhäusern kommunizieren Sie problemlos auf Englisch. Deutschsprachige Ärzte finden Sie punktuell bei Bumrungrad und Samitivej. Für die Erstkonsultation eignet sich das Stichwort „endocrinology – weight management" bzw. „obesity clinic" bei der Terminvereinbarung.',
        travelTitle: "Alternative: Medizin-Tourismus in Nachbarländer",
        travelBody:
          'Retatrutid ist auch in Singapur, Malaysia, Vietnam und Kambodscha nicht als Fertigarzneimittel verfügbar. Ein „Retatrutid-Trip" lohnt sich nur bei einem konkreten, genehmigten Studienprogramm vor Ort. Für Wegovy / Mounjaro bietet Thailand innerhalb Südostasiens die beste Verfügbarkeit.',
        ctaTitle: "Weiterlesen",
        ctaBody: "Grundlagen zum Wirkstoff und zum aktuellen weltweiten Zulassungsstatus:",
        cta1: "Zulassungs-Status weltweit",
        cta2: "Wegovy vs Mounjaro vs Retatrutid",
      }
    : {
        eyebrow: "For expats in Thailand",
        h1: "Retatrutide & GLP-1 therapy in Thailand",
        lead: "What foreigners living in Bangkok, Chiang Mai, Phuket or Pattaya should know: as of 2026, Retatrutide is not registered as a finished pharmaceutical in Thailand, but is accessible through approved clinical study programs. Wegovy, Ozempic and Mounjaro are partially available through international hospitals.",
        statusTitle: "Regulatory status in Thailand (อย. / Thai FDA)",
        status: [
          { name: "Retatrutide (LY3437943)", value: "Clinical study", ok: true, body: "Not a regular pharmacy or hospital product. Access is exclusively through approved clinical study programs, such as our partner clinic." },
          { name: "Semaglutide (Ozempic, Wegovy)", value: "Available", ok: true, body: "Ozempic is registered for T2D in Thailand; Wegovy is available through international hospitals such as Bumrungrad, BNH and Samitivej." },
          { name: "Tirzepatide (Mounjaro)", value: "Limited", ok: true, body: "Mounjaro has been distributed by Eli Lilly Thailand since 2024, but often runs low on stock. The Zepbound brand is not registered separately in Thailand." },
        ],
        clinicsTitle: "International hospitals in Bangkok with obesity clinics",
        clinics: [
          { name: "Bumrungrad International", area: "Sukhumvit / Nana", body: "English-speaking endocrinology and obesity clinics, with German- and Arabic-speaking patient coordinators in some cases." },
          { name: "BNH Hospital", area: "Silom / Sathorn", body: "Smaller expat-oriented hospital with close monitoring on GLP-1 therapies." },
          { name: "Samitivej Sukhumvit", area: "Phrom Phong", body: "Established address for long-term expats, Japanese- and English-speaking staff." },
          { name: "MedPark Hospital", area: "Rama IV", body: "Modern hospital focused on metabolic medicine and diabetes." },
        ],
        priceTitle: "Prices in Thailand (2026 reference, in THB)",
        prices: [
          { drug: "Ozempic 1.0 mg (1 pen / 4 weeks)", price: "~ 6,500 – 9,000 THB" },
          { drug: "Wegovy (starter dose)", price: "~ 8,000 – 12,000 THB / month" },
          { drug: "Mounjaro 5 mg (4 pens / month)", price: "~ 12,000 – 18,000 THB" },
          { drug: "First doctor consultation (Bumrungrad / Samitivej)", price: "~ 1,500 – 3,000 THB" },
        ],
        priceNote:
          "Prices vary by hospital, dose and stock. Private health insurance in Thailand typically does NOT cover GLP-1 therapy for weight loss — unlike for type-2 diabetes.",
        insuranceTitle: "Insurance & visa status",
        insuranceBody:
          "You do NOT need Thai health insurance to get a prescription — treatment is usually paid in cash or by credit card. An international expat plan (Cigna Global, April International, LuxMed etc.) covering at least consultations is helpful. Long-Stay, Elite or Retirement visas do not affect prescription eligibility.",
        legalTitle: "Legal notes",
        legalItems: [
          "Importing Wegovy / Ozempic / Mounjaro into Thailand requires a prescription; mail shipments are not the intended route.",
          "'Research peptides' containing Retatrutide offered online from China or Thailand are not verified medicines and are not part of an approved study program.",
          "You need a prescription from a Thai-licensed physician; use one of the international hospitals listed above.",
        ],
        langTitle: "Language at the doctor's",
        langBody:
          "At the hospitals listed you can communicate comfortably in English. German-speaking doctors are occasionally available at Bumrungrad and Samitivej. When booking, ask for 'endocrinology – weight management' or 'obesity clinic'.",
        travelTitle: "Alternative: medical travel to neighboring countries",
        travelBody:
          "Retatrutide is not available as a finished pharmaceutical in Singapore, Malaysia, Vietnam or Cambodia either. A 'Retatrutide trip' is only worthwhile if a specific approved study program exists there. For Wegovy / Mounjaro, Thailand offers the best availability in South East Asia.",
        ctaTitle: "Read next",
        ctaBody: "Background on the compound and the current worldwide approval status:",
        cta1: "Worldwide approval status",
        cta2: "Wegovy vs Mounjaro vs Retatrutide",
      };

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 md:py-20">
      <div className="max-w-3xl">
        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-indigo">
          <MapPin className="h-3.5 w-3.5" />
          {c.eyebrow}
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {c.h1}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">{c.lead}</p>
      </div>

      <HeroImage
        {...thailandClinicImage}
        alt="Modern minimalist Thai clinic consultation room with tropical daylight"
        eager
      />


      <section className="mt-12">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
          <ShieldCheck className="h-5 w-5 text-indigo" />
          {c.statusTitle}
        </h2>
        <div className="mt-4 grid gap-3">
          {c.status.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="card-elevated flex flex-col gap-2 p-5 md:flex-row md:items-center md:gap-6"
            >
              <div className="md:w-64">
                <h3 className="text-base font-semibold text-foreground">{s.name}</h3>
                <span
                  className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                    s.ok ? "bg-emerald/15 text-emerald" : "bg-warning/15 text-warning"
                  }`}
                >
                  {s.value}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
          <Hospital className="h-5 w-5 text-indigo" />
          {c.clinicsTitle}
        </h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {c.clinics.map((h) => (
            <div key={h.name} className="card-elevated p-5">
              <h3 className="text-base font-semibold text-foreground">{h.name}</h3>
              <p className="mt-0.5 text-xs font-mono uppercase tracking-widest text-indigo">{h.area}</p>
              <p className="mt-2 text-sm text-muted-foreground">{h.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14 rounded-2xl border border-border bg-card p-6 md:p-8">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
          <Wallet className="h-5 w-5 text-emerald" />
          {c.priceTitle}
        </h2>
        <ul className="mt-4 divide-y divide-border">
          {c.prices.map((p) => (
            <li key={p.drug} className="flex items-center justify-between gap-4 py-3 text-sm">
              <span className="text-foreground">{p.drug}</span>
              <span className="font-mono font-semibold text-foreground">{p.price}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs text-muted-foreground">{c.priceNote}</p>
      </section>

      <section className="mt-10 grid gap-4 md:grid-cols-2">
        <div className="card-elevated p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <ShieldCheck className="h-5 w-5 text-indigo" />
            {c.insuranceTitle}
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">{c.insuranceBody}</p>
        </div>
        <div className="card-elevated p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Languages className="h-5 w-5 text-indigo" />
            {c.langTitle}
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">{c.langBody}</p>
        </div>
      </section>

      <section className="mt-10 rounded-2xl border border-warning/40 bg-warning/5 p-6">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <AlertTriangle className="h-5 w-5 text-warning" />
          {c.legalTitle}
        </h2>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          {c.legalItems.map((it) => (
            <li key={it} className="flex gap-2">
              <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-warning" />
              {it}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-10 rounded-2xl border border-border bg-card p-6">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <Plane className="h-5 w-5 text-indigo" />
          {c.travelTitle}
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">{c.travelBody}</p>
      </section>

      <section className="mt-10 rounded-2xl border border-border bg-card p-6 md:p-8">
        <h2 className="text-lg font-semibold text-foreground">{c.ctaTitle}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{c.ctaBody}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/zulassung"
            className="inline-flex items-center rounded-md bg-indigo px-4 py-2 text-sm font-medium text-indigo-foreground hover:opacity-90"
          >
            {c.cta1}
          </Link>
          <Link
            to="/vergleich"
            className="inline-flex items-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
          >
            {c.cta2}
          </Link>
        </div>
      </section>

      <FaqBlock de={de} />

      <ConsultCta variant="section" />
      <RelatedLinks only={["/vergleich", "/sicherheit", "/faq"]} />
    </div>
  );
}

function FaqBlock({ de }: { de: boolean }) {
  const faqs = getExpatFaqs(de);
  const title = de
    ? "Häufige Fragen von Expats in Thailand"
    : "Frequently asked questions from expats in Thailand";
  const lead = de
    ? "Die wichtigsten praktischen Fragen zu Retatrutid, Wegovy, Ozempic und Mounjaro für Ausländer mit Wohnsitz in Thailand."
    : "The most important practical questions on Retatrutide, Wegovy, Ozempic and Mounjaro for foreigners living in Thailand.";

  return (
    <section className="mt-14" aria-labelledby="thailand-faq">
      <div className="flex items-center gap-2">
        <HelpCircle className="h-5 w-5 text-indigo" />
        <h2 id="thailand-faq" className="text-xl font-semibold text-foreground">
          {title}
        </h2>
      </div>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{lead}</p>

      <div className="mt-6 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
        {faqs.map((f, i) => (
          <details
            key={f.q}
            {...(i === 0 ? { open: true } : {})}
            className="group px-5 py-4 open:bg-muted/30"
          >
            <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-left">
              <span className="text-base font-semibold text-foreground">{f.q}</span>
              <span
                aria-hidden="true"
                className="mt-1 select-none font-mono text-lg leading-none text-indigo transition-transform group-open:rotate-45"
              >
                +
              </span>
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
